import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import synopsis640 from "@/assets/media/synopsis-hedwig-640.webp";
import synopsis1200 from "@/assets/media/synopsis-hedwig-1200.webp";
import synopsis1600 from "@/assets/media/synopsis-hedwig-1600.webp";

const reveal = {
  hidden: { opacity: 0, y: 60 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const throughLines = [
  { text: "through him,", accent: false },
  { text: "through they/them/us,", accent: false },
  { text: "through you... the Reader.", accent: true },
  { text: "We hope you enjoy ", highlight: "the read.", accent: false },
];

export default function Synopsis() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.02, 1.05, 1.02]);

  return (
    <section
      ref={ref}
      id="synopsis"
      data-testid="synopsis-section"
      className="relative z-10 overflow-hidden pb-0 pt-32 lg:pt-44"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-crimson/10 blur-[140px]"
      />
      <div className="mx-auto max-w-5xl px-6 sm:px-12">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="mb-10 font-mono text-xs font-bold uppercase tracking-[0.35em] text-crimson"
        >
          01 // The Signal
        </motion.p>

        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          data-testid="synopsis-headline"
          className="font-display text-3xl font-bold uppercase leading-tight tracking-tight sm:text-5xl lg:text-6xl"
        >
          A somewhat <span className="text-neon">humorous</span>, yet{" "}
          <span className="text-glow-magenta text-crimson">100% accurate</span> explanation of Andrew
          reading The&nbsp;Bay
        </motion.h2>

      </div>

      <div className="relative mt-16 flex min-h-[480px] w-full items-center overflow-hidden sm:min-h-[560px] lg:min-h-[650px]">
        <motion.img
          style={{ y: imageY, scale: imageScale }}
          src={synopsis1200}
          srcSet={`${synopsis640} 640w, ${synopsis1200} 1200w, ${synopsis1600} 1600w`}
          sizes="100vw"
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="absolute -inset-y-[18%] inset-x-0 h-[136%] w-full object-cover object-[85%_center] sm:object-center will-change-transform"
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-void/60 via-void/15 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-5xl px-6 sm:px-12">
          <div className="space-y-3 border-l-2 border-neon/70 pl-8 sm:pl-12">
            {throughLines.map((line, i) => (
              <motion.p
                key={line.text}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i + 2}
                data-testid={`synopsis-through-line-${i + 1}`}
                className={`font-display text-2xl font-light uppercase tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)] sm:text-4xl ${
                  line.accent ? "text-glow-cyan text-neon" : "text-[#F4F4F4]"
                }`}
              >
                {line.text}
                {line.highlight && (
                  <span className="text-glow-magenta text-crimson">{line.highlight}</span>
                )}
              </motion.p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
